import React, { FC } from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { IShiftScheduleDTO } from 'src/api/dtos';
import { Modal, Status } from 'src/shared/components';
import { DAY_COMMA_DATE_FORMAT } from 'src/shared/constants';
import { FeaturesEnums, PermissionsEnums, ShiftSwapStateEnums } from 'src/shared/enums';
import { ComponentWithFeatures, ComponentWithPermissions } from 'src/shared/hocs';
import { useFeatures } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { formatEmployeeName } from 'src/shared/utils';
import { RootState } from 'src/store/types';

import {
  CalendarAllocationsInfo,
  CalendarEmployeeInfo,
  CalendarNotesInfo,
  CalendarShiftSwapControls,
  CalendarTimeInfo,
} from './modal-content';

interface Props {
  item: IShiftScheduleDTO;
  open: boolean;
  onClose: () => void;
  isPickup?: boolean;
}

export const CalendarShiftModal: FC<Props> = ({ item, open, onClose, isPickup }) => {
  const { t } = useTranslate();
  const { settings } = useSelector((state: RootState) => state.user);
  const hasShiftSwapping = useFeatures(FeaturesEnums.ShiftSwapping);
  const { id, startDate, endDate, notes, employee, shiftSwap } = item;
  const formattedStartDate = moment(startDate).format(DAY_COMMA_DATE_FORMAT);
  const formattedEndDate = moment(endDate).format(DAY_COMMA_DATE_FORMAT);
  const shiftSwapEnabled = !!settings?.isShiftSwapEnabled && hasShiftSwapping;

  const message =
    formattedStartDate === formattedEndDate ? formattedStartDate : `${formattedStartDate} - ${formattedEndDate}`;

  const getType = () => {
    if (!isPickup) {
      return 'info';
    }

    return shiftSwap?.state === ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL ? 'pending' : 'danger';
  };

  return (
    <Modal
      type={getType()}
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: (
          <Stack direction="row" gap={2}>
            {t('calendar.scheduleDetails')}
            {isPickup && (
              <Chip
                variant="filled"
                color="success"
                label={t('shiftSwaps.availableShift')}
                sx={{ borderRadius: '4px', backgroundColor: '#f5f4e9', color: '#938709' }}
              />
            )}
          </Stack>
        ),
        message,
        startIcon: <Status size="large" variant={getType()} />,
        onClose,
      }}
    >
      <div className="ms-3 ps-3">
        {!!shiftSwap?.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(shiftSwap.error)}
          </Alert>
        )}
        {<CalendarTimeInfo startTime={startDate} endTime={endDate} />}
        <ComponentWithPermissions permissions={PermissionsEnums.SS_NOTES_VIEW}>
          {notes && <CalendarNotesInfo notes={[{ label: t('calendar.scheduleNote'), value: notes }]} />}
        </ComponentWithPermissions>
        <ComponentWithFeatures
          features={[
            FeaturesEnums.DepartmentAllocation,
            FeaturesEnums.JobCosting,
            FeaturesEnums.SchedulingLabourAllocation,
          ]}
        >
          <CalendarAllocationsInfo labourAllocations={item.labourAllocations} />
        </ComponentWithFeatures>
        {isPickup && employee && <CalendarEmployeeInfo name={formatEmployeeName(employee)} isDropper />}
        {shiftSwapEnabled && shiftSwap && (
          <CalendarShiftSwapControls
            shiftScheduleId={id}
            shiftSwap={shiftSwap}
            startDate={startDate}
            endDate={endDate}
            onClose={onClose}
            isPickup={isPickup}
          />
        )}
      </div>
    </Modal>
  );
};
